"use client"

import { useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
  Autocomplete,
  Chip,
} from "@mui/material"
import SaveIcon from "@mui/icons-material/Save"
import { ThemeProvider } from "@mui/material/styles"
import {
  RRHH_TIPOS_JORNADA,
  extractErrorMessage,
  formatCurrency,
  formatFecha,
  rrhhTheme,
} from "./rrhhUi.js"

const buildEmptyContrato = () => ({
  contrato: {
    fechaVigenciaDesde: "",
    fechaVigenciaHasta: "",
    puesto: "",
    tipoJornada: "",
    sueldoBase: "",
    pagoPorHora: "",
    pagoPorHoraExtra: "",
    sindicatoGremio: "",
    categoria: "",
    observaciones: "",
  },
  cerrarContratoAnterior: true,
})

const ModalRrhhContrato = ({
  open,
  empleadoNombre,
  contratoVigente,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState(buildEmptyContrato())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) {
      setSaving(false)
      setError(null)
      setFormData(buildEmptyContrato())
    }
  }, [open])

  const handleContratoChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      contrato: {
        ...prev.contrato,
        [field]: event.target.value,
      },
    }))
  }

  const handleSubmit = async () => {
    if (!formData.contrato.fechaVigenciaDesde) {
      setError("La fecha de vigencia desde es requerida.")
      return
    }

    try {
      setSaving(true)
      setError(null)
      await onSave({
        contrato: formData.contrato,
        cerrarContratoAnterior: formData.cerrarContratoAnterior,
      })
    } catch (submitError) {
      setError(extractErrorMessage(submitError, "No se pudo guardar el contrato."))
    } finally {
      setSaving(false)
    }
  }

  return (
    <ThemeProvider theme={rrhhTheme}>
      <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Nuevo contrato
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {empleadoNombre || "Empleado"} • historial contractual RRHH
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={3}>
            {error ? <Alert severity="error">{error}</Alert> : null}

            {contratoVigente ? (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Contrato vigente actual
                </Typography>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} flexWrap="wrap" useFlexGap>
                  <Chip label={`Desde ${formatFecha(contratoVigente.fechaVigenciaDesde)}`} />
                  {contratoVigente.puesto ? <Chip label={`Puesto: ${contratoVigente.puesto}`} /> : null}
                  {contratoVigente.tipoJornada ? <Chip label={`Jornada: ${contratoVigente.tipoJornada}`} /> : null}
                  {contratoVigente.sueldoBase ? <Chip label={`Sueldo: ${formatCurrency(contratoVigente.sueldoBase)}`} /> : null}
                </Stack>
              </Box>
            ) : null}

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Vigencia desde"
                  type="date"
                  value={formData.contrato.fechaVigenciaDesde}
                  onChange={handleContratoChange("fechaVigenciaDesde")}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Vigencia hasta"
                  type="date"
                  value={formData.contrato.fechaVigenciaHasta}
                  onChange={handleContratoChange("fechaVigenciaHasta")}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Puesto"
                  value={formData.contrato.puesto}
                  onChange={handleContratoChange("puesto")}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={RRHH_TIPOS_JORNADA}
                  value={RRHH_TIPOS_JORNADA.find((option) => option.value === formData.contrato.tipoJornada) || null}
                  onChange={(_, option) => {
                    setFormData((prev) => ({
                      ...prev,
                      contrato: {
                        ...prev.contrato,
                        tipoJornada: option?.value || "",
                      },
                    }))
                  }}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => <TextField {...params} label="Tipo jornada" fullWidth />}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Categoría"
                  value={formData.contrato.categoria}
                  onChange={handleContratoChange("categoria")}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Sindicato / Gremio"
                  value={formData.contrato.sindicatoGremio}
                  onChange={handleContratoChange("sindicatoGremio")}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Sueldo base"
                  value={formData.contrato.sueldoBase}
                  onChange={handleContratoChange("sueldoBase")}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Pago por hora"
                  value={formData.contrato.pagoPorHora}
                  onChange={handleContratoChange("pagoPorHora")}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Pago por hora extra"
                  value={formData.contrato.pagoPorHoraExtra}
                  onChange={handleContratoChange("pagoPorHoraExtra")}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Observaciones"
                  value={formData.contrato.observaciones}
                  onChange={handleContratoChange("observaciones")}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.cerrarContratoAnterior}
                  onChange={(event) => {
                    setFormData((prev) => ({
                      ...prev,
                      cerrarContratoAnterior: event.target.checked,
                    }))
                  }}
                />
              }
              label="Cerrar automáticamente el contrato anterior abierto"
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} disabled={saving}>
            {saving ? "Guardando..." : "Guardar contrato"}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  )
}

export default ModalRrhhContrato
